import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Divider,
  Button,
  Badge,
  Flex,
  Icon,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useSearchParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import TesseramentoDetails from './TesseramentoDetails';
import { useAuth } from '../../contexts/AuthContext';
import TesseramentoForm from '../TesseramentoForm';
import PaymentForm from '../PaymentForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const Tesseramenti = () => {
  const { userData, requestUserData } = useAuth();
  const [searchParams] = useSearchParams();
  const remotePaymentStatus = searchParams.get('redirect_status');
  const [paymentStatus, setPaymentStatus] = useState('');
  const remoteSocioId = searchParams.get('id');
  const [showForm, setShowForm] = useState(false);
  const [showParentForm, setShowParentForm] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const {
    isOpen: isRinnovoModalOpen,
    onOpen: onRinnovoModalOpen,
    onClose: onRinnovoModalClose,
  } = useDisclosure();
  const {
    isOpen: isPaymentModalOpen,
    onOpen: onPaymentModalOpen,
    onClose: onPaymentModalClose,
  } = useDisclosure();
  const [isCreatingRinnovo, setIsCreatingRinnovo] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [clientPaymentIntentId, setClientPaymentIntentId] = useState('');
  const [loadingSecret, setLoadingSecret] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [socioForPayment, setSocioForPayment] = useState(null);
  const [scrollToDetailsRequested, setScrollToDetailsRequested] =
    useState(false);
  const detailsSectionRef = useRef(null);
  const toast = useToast();

  const soci = userData?.tesseramenti || []; // Retrocompatibilità: userData.tesseramenti contiene i soci

  // Raggruppa tesseramenti per socio
  const tesseramentiBySocio = soci.map((socio) => {
    const tesseramentiSocio =
      socio.tesseramenti && Array.isArray(socio.tesseramenti)
        ? socio.tesseramenti
        : [];
    return {
      socio,
      tesseramenti: tesseramentiSocio.sort(
        (a, b) => (b.anno || 0) - (a.anno || 0)
      ), // Ordina per anno decrescente
    };
  });

  // Anno corrente per il rinnovo
  const currentYear = new Date().getFullYear();

  // Verifica quali soci possono rinnovare per l'anno corrente (non hanno già un tesseramento per l'anno corrente)
  const sociConRinnovoDisponibile = tesseramentiBySocio.filter(
    ({ tesseramenti }) => {
      const hasTesseramentoCurrentYear = tesseramenti.some(
        (t) => t.anno === currentYear
      );
      return !hasTesseramentoCurrentYear;
    }
  );

  const canRinnovare = sociConRinnovoDisponibile.length > 0;

  // Check if user has any minor children registered
  const hasMinorChildren = soci.some((socio) => socio.isMinorenne === true);

  // Check if user already has an adult socio registered
  const hasAdultSocio = soci.some((socio) => socio.isMinorenne === false);

  // Show parent registration card only if user has minor children but no adult socio
  const showParentRegistration = hasMinorChildren && !hasAdultSocio;

  const onPaymentSuccess = () => {
    setSelectedIndex(null);
  };

  const onSubmitSuccess = () => {
    setShowForm(false);
  };

  const onParentSubmitSuccess = () => {
    setShowParentForm(false);
  };

  const onModalClose = () => {
    setShowForm(false);
    setShowParentForm(false);
    setSelectedIndex();
  };

  const handleDetailsClick = (index) => {
    setShowForm(false);
    setShowParentForm(false);
    setSelectedIndex(index);
    setPaymentStatus(''); // Reset payment status per evitare apertura automatica del drawer
    setScrollToDetailsRequested(true);
  };

  const handlePayNowClick = (index) => {
    setShowForm(false);
    setShowParentForm(false);
    setSelectedIndex(index);
    // Forza l'apertura del drawer di pagamento impostando un paymentStatus che non è 'succeeded'
    setPaymentStatus('pending');
  };

  const createPaymentIntentForRinnovo = async (socio) => {
    if (!socio) return;

    setLoadingSecret(true);
    setPaymentError('');
    setSocioForPayment(socio);

    try {
      const response = await axios.post('/api/create-payment-intent', {
        amount: 1000, // 10€ in centesimi
        currency: 'eur',
        socioId: socio.documentId,
        stripeCustomerId: userData?.stripeCustomerId,
        eventVariants: {
          [socio.documentId]: {
            name: `Rinnovo Tessera ${currentYear} - ${socio.nomeSocio}`,
            type: 'tesseramento',
            priceOption: 'totale',
            amount: 10,
          },
        },
        metadata: {
          type: 'rinnovo_tessera',
          year: currentYear,
        },
      });

      if (response.data && response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
        setClientPaymentIntentId(
          response.data.paymentIntentId || response.data.localPaymentIntentId
        );
        setShowPaymentForm(true);
        onPaymentModalOpen();
      } else {
        throw new Error('Client secret non ricevuto dal server');
      }
    } catch (error) {
      console.error('Error creating PaymentIntent:', error);
      setPaymentError(
        error.response?.data?.error ||
          error.message ||
          'Errore nella creazione del pagamento'
      );
      toast({
        title: 'Errore',
        description:
          error.response?.data?.error ||
          error.message ||
          'Errore nella creazione del pagamento',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingSecret(false);
    }
  };

  const createRinnovo = async (socio, paymentData) => {
    if (!socio) return;

    setIsCreatingRinnovo(true);
    try {
      // Usa paymentIntentId dal paymentData se disponibile, altrimenti usa quello locale
      const paymentIntentId =
        paymentData?.paymentIntentId || clientPaymentIntentId;
      const localPaymentIntentId = clientPaymentIntentId;

      // Procediamo direttamente con la creazione dell'ordine
      // Se l'ordine esiste già (creato dal webhook), il backend gestirà il duplicato
      // Se l'ordine esiste già (creato dal webhook), il backend gestirà il duplicato
      const response = await axios.post('/api/ordini', {
        participantId: socio.documentId,
        paymentIntentId,
        paymentStatus: paymentData?.paymentStatus || 'succeeded',
        paymentMethod: paymentData?.paymentMethod || 'elettronico',
        paymentMethodId: paymentData?.paymentMethodId || null,
        amount: 10,
        paymentIntents: localPaymentIntentId || paymentIntentId,
        year: currentYear,
        eventVariants: {
          [socio.documentId]: {
            type: 'tesseramento',
            priceOption: 'totale',
            amount: 10,
            name: `Rinnovo Tessera ${currentYear} - ${socio.nomeSocio}`,
          },
        },
      });

      if (response.status !== 200) {
        throw new Error('Errore nella creazione del rinnovo');
      }

      setPaymentSuccess(true);
      toast({
        title: 'Rinnovo creato',
        description: `Rinnovo tessera ${currentYear} creato per ${socio.nomeSocio} ${socio.cognomeSocio}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Chiudi il modale dopo il successo
      setTimeout(() => {
        onPaymentModalClose();
        setShowPaymentForm(false);
        setClientSecret('');
        setClientPaymentIntentId('');
        setPaymentSuccess(false);
        // Ricarica i dati utente senza refresh della pagina
        requestUserData({ withLoading: false });
      }, 2000);
    } catch (error) {
      console.error('Errore nella creazione del rinnovo:', error);
      setPaymentError(
        error.response?.data?.error ||
          error.message ||
          'Impossibile creare il rinnovo'
      );
      toast({
        title: 'Errore',
        description:
          error.response?.data?.error ||
          error.message ||
          'Impossibile creare il rinnovo',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingRinnovo(false);
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    if (socioForPayment) {
      // Se il pagamento è già stato processato dal webhook, l'ordine potrebbe già esistere
      // Verifica prima se esiste, altrimenti crealo
      createRinnovo(socioForPayment, paymentData);
    }
  };

  const handlePaymentError = (error) => {
    // Gestisci errori di pagamento, incluso il caso in cui il PaymentIntent è già stato confermato
    if (error?.code === 'payment_intent_unexpected_state') {
      // Il PaymentIntent è già stato confermato, probabilmente dal webhook
      // Il webhook ha già creato l'ordine, quindi possiamo considerarlo un successo
      if (socioForPayment) {
        setPaymentSuccess(true);
        toast({
          title: 'Pagamento già processato',
          description:
            'Il pagamento è già stato processato correttamente. Il tesseramento verrà creato automaticamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        setTimeout(() => {
          onPaymentModalClose();
          setShowPaymentForm(false);
          setClientSecret('');
          setClientPaymentIntentId('');
          setPaymentSuccess(false);
          // Ricarica i dati utente senza refresh della pagina
          requestUserData({ withLoading: false });
        }, 2000);
      }
    } else {
      setPaymentError(error?.message || 'Errore durante il pagamento');
      toast({
        title: 'Errore pagamento',
        description: error?.message || 'Errore durante il pagamento',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleRinnovo = () => {
    if (sociConRinnovoDisponibile.length === 1) {
      // Un solo socio, procedi direttamente con il pagamento
      createPaymentIntentForRinnovo(sociConRinnovoDisponibile[0].socio);
    } else {
      // Più soci, mostra modale per selezionare
      onRinnovoModalOpen();
    }
  };

  const handleSelectSocioForRinnovo = (socio) => {
    onRinnovoModalClose();
    createPaymentIntentForRinnovo(socio);
  };

  const handlePaymentModalClose = () => {
    onPaymentModalClose();
    setShowPaymentForm(false);
    setPaymentError('');
    setClientSecret('');
    setClientPaymentIntentId('');
    setSocioForPayment(null);
    setPaymentSuccess(false);
  };

  useEffect(() => {
    if (remotePaymentStatus !== 'succeeded' || !remoteSocioId) return undefined;

    const socioIndex = soci.findIndex((s) => s.documentId === remoteSocioId);
    const remotePaymentIntentId = searchParams.get('paymentIntentId');

    setPaymentStatus('succeeded');
    if (socioIndex >= 0) {
      setSelectedIndex(socioIndex);
    }

    let cancelled = false;

    const finalizePayment = async () => {
      if (remotePaymentIntentId) {
        try {
          await axios.post('/api/ordini', {
            participantId: remoteSocioId,
            paymentIntentId: remotePaymentIntentId,
            paymentStatus: 'succeeded',
            paymentMethod: 'elettronico',
            amount: 10,
            year: currentYear,
            eventVariants: {
              [remoteSocioId]: {
                type: 'tesseramento',
                priceOption: 'totale',
                amount: 10,
                name: 'Tesseramento',
              },
            },
          });
        } catch (error) {
          console.error(
            'Finalizzazione ordine tesseramento post-redirect:',
            error.response?.data || error.message
          );
        }
      }

      if (cancelled) return;

      window.history.replaceState({}, '', '/account/tesseramenti');
      requestUserData({ withLoading: false });
    };

    const timeoutId = setTimeout(finalizePayment, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    remotePaymentStatus,
    soci,
    remoteSocioId,
    requestUserData,
    searchParams,
    currentYear,
  ]);

  useEffect(() => {
    if (!scrollToDetailsRequested || selectedIndex === null) return;

    setTimeout(() => {
      if (detailsSectionRef.current) {
        const offset = window.innerWidth < 768 ? 110 : 80;
        const targetTop =
          detailsSectionRef.current.getBoundingClientRect().top +
          window.scrollY -
          offset;

        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth',
        });
      }
      setScrollToDetailsRequested(false);
    }, 120);
  }, [scrollToDetailsRequested, selectedIndex]);

  return (
    <Box>
      <Box
        as="span"
        bg="#2f3136"
        p={{ base: 6, md: 8 }}
        display="block"
        borderRadius="20px"
        boxShadow="0 20px 40px rgba(0,0,0,0.3)"
        border="1px solid"
        borderColor="gray.600"
        minH={{ base: 'unset', md: '532px' }}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Heading
            size="lg"
            color="white"
            id="tesseramenti-title"
            fontWeight="800"
          >
            I Tuoi Tesseramenti
          </Heading>
          {canRinnovare && (
            <Button
              bg="#ffcc00"
              color="black"
              borderRadius="6px"
              fontWeight="600"
              size="sm"
              _hover={{
                bg: '#ffdd44',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(255, 204, 0, 0.3)',
              }}
              transition="all 0.3s ease"
              onClick={handleRinnovo}
              isLoading={isCreatingRinnovo}
              loadingText="Creando..."
            >
              Rinnovo Tessera {currentYear}
            </Button>
          )}
        </Flex>
        <Divider mb={6} borderColor="gray.600" />

        {/* Griglia delle tessere */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6}>
          {tesseramentiBySocio.map(
            ({ socio, tesseramenti: tesseramentiSocio }, index) => {
              // Mostra "Paga Ora" solo se manca il pagamento per l'anno corrente
              const currentYearTessera = tesseramentiSocio.find(
                (t) => Number(t.anno) === currentYear
              );
              const hasPendingTesseramento = currentYearTessera
                ? currentYearTessera.paymentStatus !== 'succeeded'
                : socio.paymentStatus === 'pending' ||
                  tesseramentiSocio.length === 0;

              return (
                <Box
                  key={socio.documentId}
                  p={5}
                  borderRadius="6px"
                  boxShadow="sm"
                  transition="all 0.2s ease"
                  bg={selectedIndex === index ? '#2f3136' : '#36393f'}
                  borderColor={selectedIndex === index ? '#ffcc00' : 'gray.700'}
                  borderWidth="1px"
                  _hover={{
                    borderColor: '#ffcc00',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <Box>
                    <Text
                      fontWeight="600"
                      fontSize="md"
                      color="white"
                      mb={3}
                      data-clarity-mask="True"
                    >
                      {socio.nomeSocio || 'Nome non disponibile'}
                    </Text>
                    {tesseramentiSocio.length > 0 && (
                      <VStack align="stretch" spacing={2} mt={3}>
                        {tesseramentiSocio.slice(0, 3).map((t) => (
                          <Flex
                            key={t.documentId || t.id}
                            justify="space-between"
                            align="center"
                            py={1}
                          >
                            <Flex align="center" gap={2}>
                              <Text
                                color="gray.400"
                                fontSize="sm"
                                fontWeight="500"
                              >
                                {t.anno || 'N/A'}
                              </Text>
                              <Text color="gray.500" fontSize="xs">
                                Numero tessera:{' '}
                                {t.numero || socio.Numero_Tessera || 'N/A'}
                              </Text>
                            </Flex>
                            <Box
                              w="8px"
                              h="8px"
                              borderRadius="full"
                              bg={
                                t.paymentStatus === 'succeeded'
                                  ? '#3eb297'
                                  : t.paymentStatus === 'failed'
                                    ? '#e73341'
                                    : '#ffcc00'
                              }
                            />
                          </Flex>
                        ))}
                        {tesseramentiSocio.length > 3 && (
                          <Text
                            color="gray.500"
                            fontSize="xs"
                            textAlign="center"
                            pt={1}
                          >
                            +{tesseramentiSocio.length - 3} altri
                          </Text>
                        )}
                      </VStack>
                    )}
                  </Box>
                  <Flex
                    align="center"
                    justify="flex-end"
                    mt={4}
                    pt={3}
                    borderTop="1px solid"
                    borderColor="gray.700"
                    gap={2}
                  >
                    {hasPendingTesseramento && (
                      <Badge
                        as="button"
                        bg="#ffcc00"
                        color="black"
                        borderRadius="4px"
                        px={3}
                        py={1}
                        fontSize="xs"
                        fontWeight="500"
                        cursor="pointer"
                        _hover={{
                          bg: '#ffdd44',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(255, 204, 0, 0.3)',
                        }}
                        transition="all 0.2s ease"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePayNowClick(index);
                        }}
                      >
                        Paga Ora
                      </Badge>
                    )}
                    <Badge
                      as="button"
                      bg="#ffcc00"
                      color="black"
                      borderRadius="4px"
                      px={3}
                      py={1}
                      fontSize="xs"
                      fontWeight="500"
                      cursor="pointer"
                      _hover={{
                        bg: '#ffdd44',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(255, 204, 0, 0.3)',
                      }}
                      transition="all 0.2s ease"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDetailsClick(index);
                      }}
                    >
                      Dettagli
                    </Badge>
                  </Flex>
                </Box>
              );
            }
          )}

          {/* Card per aggiungere una nuova tessera */}
          <Box
            p={5}
            borderRadius="6px"
            boxShadow="sm"
            transition="all 0.2s ease"
            bg="#36393f"
            borderWidth="1px"
            borderColor="#36a9e1"
            borderStyle="dashed"
            _hover={{
              borderColor: '#ffcc00',
              boxShadow: '0 4px 12px rgba(54, 169, 225, 0.2)',
            }}
            cursor="pointer"
            onClick={() => {
              setShowForm(true);
              setShowParentForm(false);
              setSelectedIndex(null);
            }}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
          >
            <Icon as={AddIcon} boxSize={10} color="#ffcc00" />
            <Text mt={2} fontWeight="bold" color="white">
              Nuova Tessera Minore
            </Text>
          </Box>

          {/* Card per aggiungere tessera genitore (solo se ha figli minori ma non ha ancora un socio adulto) */}
          {showParentRegistration && (
            <Box
              p={5}
              borderRadius="6px"
              boxShadow="sm"
              transition="all 0.2s ease"
              bg="#36393f"
              borderWidth="1px"
              borderColor="#e73341"
              borderStyle="dashed"
              _hover={{
                borderColor: '#ffcc00',
                boxShadow: '0 4px 12px rgba(231, 51, 65, 0.2)',
              }}
              cursor="pointer"
              onClick={() => {
                setShowParentForm(true);
                setShowForm(false);
                setSelectedIndex(null);
              }}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <Icon as={AddIcon} boxSize={10} color="#ffcc00" />
              <Text mt={2} fontWeight="bold" color="white">
                Nuova Tessera Genitore
              </Text>
            </Box>
          )}
        </SimpleGrid>

        {showForm && (
          <TesseramentoForm
            isAuthenticated
            userData={userData}
            onSubmitSuccess={onSubmitSuccess}
          />
        )}

        {showParentForm && (
          <TesseramentoForm
            isAuthenticated
            userData={userData}
            onSubmitSuccess={onParentSubmitSuccess}
            isParentRegistration
          />
        )}

        {/* Dettagli della tessera selezionata */}
        {selectedIndex !== null && soci[selectedIndex] && (
          <Box ref={detailsSectionRef} mt={8}>
            <TesseramentoDetails
              remotePaymentStatus={paymentStatus}
              socio={soci[selectedIndex]}
              onPaymentSuccess={onPaymentSuccess}
              onModalClose={onModalClose}
              autoOpenDrawer={paymentStatus === 'pending'}
            />

            {/* Mostra i tesseramenti per anno del socio selezionato */}
            {tesseramentiBySocio[selectedIndex]?.tesseramenti.length > 0 && (
              <Box
                mt={6}
                p={{ base: 4, md: 6 }}
                bg="#36393f"
                borderRadius="10px"
                border="1px solid"
                borderColor="gray.600"
                boxShadow="inset 0 2px 4px rgba(0,0,0,0.2)"
              >
                <Flex align="center" mb={5}>
                  <Heading size="md" color="white" fontWeight="700" flex="1">
                    Storico Tesseramenti
                  </Heading>
                  <Badge
                    bg="#ffcc00"
                    color="black"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="xs"
                    fontWeight="600"
                  >
                    {tesseramentiBySocio[selectedIndex].tesseramenti.length}{' '}
                    {tesseramentiBySocio[selectedIndex].tesseramenti.length ===
                    1
                      ? 'tessera'
                      : 'tessere'}
                  </Badge>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {tesseramentiBySocio[selectedIndex].tesseramenti.map((t) => {
                    const isPaid = t.paymentStatus === 'succeeded';
                    const isFailed = t.paymentStatus === 'failed';
                    const statusColor = isPaid
                      ? '#3eb297'
                      : isFailed
                        ? '#e73341'
                        : '#ffcc00';
                    const statusText = isPaid
                      ? 'Pagato'
                      : isFailed
                        ? 'Fallito'
                        : 'In attesa';

                    return (
                      <Box
                        key={t.documentId || t.id}
                        p={5}
                        bg="#2f3136"
                        borderRadius="8px"
                        borderWidth="2px"
                        borderColor={
                          isPaid ? '#3eb297' : isFailed ? '#e73341' : 'gray.600'
                        }
                        transition="all 0.3s ease"
                        _hover={{
                          borderColor: statusColor,
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 12px ${statusColor}40`,
                        }}
                        position="relative"
                        overflow="hidden"
                      >
                        {/* Indicatore di stato colorato in alto */}
                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          right={0}
                          h="4px"
                          bg={statusColor}
                        />

                        <VStack align="stretch" spacing={3}>
                          <Flex justify="space-between" align="flex-start">
                            <Box>
                              <Text
                                fontWeight="700"
                                color="white"
                                fontSize="xl"
                                mb={1}
                              >
                                {t.anno || 'N/A'}
                              </Text>
                              <Text
                                color="gray.400"
                                fontSize="sm"
                                fontWeight="500"
                              >
                                Anno
                              </Text>
                            </Box>
                            <Box
                              w="12px"
                              h="12px"
                              borderRadius="full"
                              bg={statusColor}
                              boxShadow={`0 0 8px ${statusColor}80`}
                              flexShrink={0}
                              mt={1}
                            />
                          </Flex>

                          <Divider borderColor="gray.600" />

                          <VStack align="stretch" spacing={2}>
                            <Box>
                              <Text
                                color="gray.500"
                                fontSize="xs"
                                fontWeight="600"
                                mb={1}
                              >
                                NUMERO TESSERA
                              </Text>
                              <Text
                                color="white"
                                fontSize="md"
                                fontWeight="600"
                              >
                                {t.numero ||
                                  soci[selectedIndex].Numero_Tessera ||
                                  'N/A'}
                              </Text>
                            </Box>

                            <Box>
                              <Text
                                color="gray.500"
                                fontSize="xs"
                                fontWeight="600"
                                mb={1}
                              >
                                STATO PAGAMENTO
                              </Text>
                              <Badge
                                bg={statusColor}
                                color={isPaid || isFailed ? 'white' : 'black'}
                                borderRadius="6px"
                                px={3}
                                py={1}
                                fontSize="xs"
                                fontWeight="600"
                                textTransform="uppercase"
                              >
                                {statusText}
                              </Badge>
                            </Box>
                          </VStack>
                        </VStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>
            )}

            <Divider my={6} borderColor="gray.600" />

            <Flex justify="flex-end" gap={3}>
              <Button
                onClick={() => setSelectedIndex(null)}
                bg="#36393f"
                color="white"
                borderRadius="8px"
                fontWeight="600"
                fontSize="sm"
                px={6}
                _hover={{
                  bg: '#40444b',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
                transition="all 0.2s ease"
              >
                Chiudi Dettagli
              </Button>
            </Flex>
          </Box>
        )}

        {/* Modale per selezionare il socio per il rinnovo */}
        <Modal
          isOpen={isRinnovoModalOpen}
          onClose={onRinnovoModalClose}
          size="md"
        >
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent bg="#2f3136" color="white">
            <ModalHeader>Seleziona Socio per Rinnovo {currentYear}</ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody>
              <Text mb={4} color="gray.300">
                Seleziona il socio per cui vuoi creare il rinnovo tessera{' '}
                {currentYear}:
              </Text>
              <VStack spacing={3} align="stretch">
                {sociConRinnovoDisponibile.map(({ socio }) => (
                  <Button
                    key={socio.documentId}
                    onClick={() => handleSelectSocioForRinnovo(socio)}
                    bg="#36393f"
                    color="white"
                    borderWidth="1px"
                    borderColor="gray.600"
                    _hover={{
                      bg: '#40444b',
                      borderColor: '#ffcc00',
                    }}
                    isDisabled={isCreatingRinnovo}
                  >
                    <Flex direction="column" align="flex-start" width="100%">
                      <Text fontWeight="bold">
                        {socio.nomeSocio} {socio.cognomeSocio}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        Tessera n. {socio.Numero_Tessera || 'N/A'}
                      </Text>
                    </Flex>
                  </Button>
                ))}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={onRinnovoModalClose}
                bg="#36393f"
                color="white"
                _hover={{ bg: '#40444b' }}
                isDisabled={isCreatingRinnovo}
              >
                Annulla
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Modale per il pagamento del rinnovo */}
        <Modal
          isOpen={isPaymentModalOpen}
          onClose={handlePaymentModalClose}
          size="md"
        >
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent bg="#2f3136" color="white">
            <ModalHeader>Rinnovo Tessera {currentYear}</ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody>
              {socioForPayment && (
                <Box mb={4} p={4} bg="#36393f" borderRadius="8px">
                  <Text fontWeight="bold" mb={2} color="white">
                    Socio: {socioForPayment.nomeSocio}{' '}
                    {socioForPayment.cognomeSocio}
                  </Text>
                  <Text color="gray.300" fontSize="sm" mt={1}>
                    Importo: €10.00
                  </Text>
                </Box>
              )}

              {paymentError && (
                <Alert status="error" borderRadius="8px" mb={4}>
                  <AlertIcon />
                  <AlertTitle>Errore!</AlertTitle>
                  <AlertDescription>{paymentError}</AlertDescription>
                </Alert>
              )}

              {paymentSuccess && (
                <Alert status="success" borderRadius="8px" mb={4}>
                  <AlertIcon />
                  <AlertTitle>Successo!</AlertTitle>
                  <AlertDescription>
                    Rinnovo tessera {currentYear} creato con successo!
                  </AlertDescription>
                </Alert>
              )}

              {showPaymentForm &&
                clientSecret &&
                clientSecret.startsWith('pi_') && (
                  <Box p={4} bg="#36393f" borderRadius="8px">
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <PaymentForm
                        ambito="tessera"
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                      />
                    </Elements>
                  </Box>
                )}

              {loadingSecret && !showPaymentForm && (
                <Box p={4} bg="#36393f" borderRadius="8px" textAlign="center">
                  <Text color="white">Preparando il pagamento...</Text>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={handlePaymentModalClose}
                bg="#36393f"
                color="white"
                _hover={{ bg: '#40444b' }}
                isDisabled={isCreatingRinnovo || loadingSecret}
              >
                {paymentSuccess ? 'Chiudi' : 'Annulla'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default Tesseramenti;
